import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { useToast } from '../components/ui/use-toast';
import GuardianDeclarationDialog from '../components/guardian/GuardianDeclarationDialog';
import { useAuth } from '../hooks/useAuth';
import {
  guardianshipsService,
  type OpenedWard,
  type PendingWard,
  type PendingWards,
} from '../services/guardianships';

/**
 * Where the letter to a parent or guardian lands: the children entered on
 * their address, and the act of opening a managed account for each.
 *
 * The guardian is a member, not staff, so this lives in the member app. The
 * token in the URL is the entry's own and proves control of the inbox the
 * organiser named — which is why a signed-in account with a different address
 * (the other parent) is still allowed through.
 *
 * Signed out, the page still says who is waiting, then sends the visitor to
 * open their own account (or sign in) and come back, the token kept in the
 * URL. Signed in, every child is one button, and every button is behind the
 * guardian's declaration: that is the consent the law asks for, and it is
 * recorded server-side with the version of the policy text it was given to.
 */
export default function GuardianAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const invitationToken = searchParams.get('invitation_token');
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { isAuthenticated, loading: authLoading, user } = useAuth();

  const [pending, setPending] = useState<PendingWards | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [opened, setOpened] = useState<OpenedWard[]>([]);
  const [confirming, setConfirming] = useState<PendingWard | null>(null);
  const [saving, setSaving] = useState(false);

  const here = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!token) {
      setTokenError(t('guardian.accept.tokenMissing'));
      setLoading(false);
      return;
    }

    let cancelled = false;
    guardianshipsService
      .pending(token)
      .then((data) => {
        if (!cancelled) setPending(data);
      })
      .catch((error) => {
        if (!cancelled) setTokenError(error instanceof Error ? error.message : t('guardian.accept.tokenInvalid'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Re-read when the viewer changes: signed in, the server also says which
    // children already have an account with this guardian.
  }, [token, isAuthenticated, t]);

  const confirmOpen = async () => {
    if (!confirming || saving) return;
    setSaving(true);
    try {
      const result = await guardianshipsService.open({
        token,
        participantId: confirming.participant_id,
        wardId: confirming.existing_ward_id,
        locale: i18n.language,
      });
      setOpened((current) => [...current, result]);
      setPending((current) =>
        current ? { ...current, wards: current.wards.filter((w) => w.key !== confirming.key) } : current
      );
      setConfirming(null);
      toast({ title: t('guardian.accept.opened', { name: result.ward.full_name }) });
    } catch (error) {
      toast({
        title: t('guardian.accept.openError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="my-8 flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (tokenError || !pending) {
    return (
      <div className="my-8 flex justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('guardian.accept.invalidTitle')}</CardTitle>
            <CardDescription>{tokenError ?? t('guardian.accept.tokenInvalid')}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/" className="w-full">
              <Button variant="outline" className="w-full">
                {t('notFound.backToHome')}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const names = pending.wards.map((w) => w.full_name);

  return (
    <div className="my-8 flex justify-center px-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{t('guardian.accept.title', { event: pending.event_name })}</CardTitle>
          <CardDescription>
            {pending.wards.length > 0
              ? t('guardian.accept.description', { names: names.join(', '), email: pending.guardian_email })
              : t('guardian.accept.nothingPending')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* What just happened, kept on the page so a parent with twins sees
              the first one done while opening the second. */}
          {opened.map((result) => (
            <div
              key={result.ward.id}
              className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm"
              data-testid="opened-ward"
            >
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
              <div>
                <p className="font-medium">{t('guardian.accept.openedFor', { name: result.ward.full_name })}</p>
                <ul className="mt-1 text-muted-foreground">
                  {result.entries.map((entry) => (
                    <li key={entry.id}>
                      <Link to={`/competitions/${entry.competition_slug}`} className="underline underline-offset-2">
                        {entry.competition_name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          {!isAuthenticated && pending.wards.length > 0 && (
            <div className="space-y-3 text-sm">
              <p>{t('guardian.accept.whyYou')}</p>
              <p className="text-muted-foreground">{t('guardian.accept.signInFirst')}</p>
            </div>
          )}

          {pending.wards.map((ward) => (
            <div
              key={ward.key}
              className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid="pending-ward"
            >
              <div className="min-w-0">
                <p className="font-medium">{ward.full_name}</p>
                <p className="text-xs text-muted-foreground">
                  {t('guardian.accept.bornOn', { date: formatDate(ward.birth_date, i18n.language) })}
                  {' · '}
                  {ward.entries.map((entry) => entry.competition_name).join(', ')}
                </p>
              </div>
              {/* Only somebody signed in can consent: the act has to be an
                  account's. Signed out, the child is named and nothing more. */}
              {isAuthenticated && (
                <Button size="sm" onClick={() => setConfirming(ward)}>
                  {ward.existing_ward_id
                    ? t('guardian.accept.thisIs', { name: ward.name ?? ward.full_name })
                    : t('guardian.accept.openFor', { name: ward.name ?? ward.full_name })}
                </Button>
              )}
            </div>
          ))}
        </CardContent>

        {!isAuthenticated && pending.wards.length > 0 && (
          <CardFooter className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="w-full"
              onClick={() =>
                invitationToken
                  ? navigate(
                      `/invitation/accept?invitation_token=${encodeURIComponent(invitationToken)}&redirect=${encodeURIComponent(here)}`
                    )
                  : navigate('/sign-up', { state: { from: here } })
              }
            >
              {t('guardian.accept.createAccount')}
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/sign-in', { state: { from: here } })}>
              {t('auth.signIn')}
            </Button>
          </CardFooter>
        )}

        {isAuthenticated && user && (
          <CardFooter>
            <p className="text-xs text-muted-foreground">
              {t('guardian.accept.signedInAs', { email: user.email })}
            </p>
          </CardFooter>
        )}
      </Card>

      {confirming && (
        <GuardianDeclarationDialog
          name={confirming.name ?? confirming.full_name}
          existing={!!confirming.existing_ward_id}
          saving={saving}
          onOpenChange={(open) => {
            if (!open) setConfirming(null);
          }}
          onConfirm={confirmOpen}
        />
      )}
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}
